import { Alert } from "@jugl-web/ui-components/cross-platform/Alert";
import { useTranslations } from "@jugl-web/utils";
import { FC, useMemo } from "react";
import planeGif from "./assets/plane.gif";

interface AuthDiscardConfirmationAlertProps {
  isOpen: boolean;
  variant: "workspace" | "profile" | "logout";
  onConfirm: () => void;
  onClose: () => void;
}

export const AuthDiscardConfirmationAlert: FC<
  AuthDiscardConfirmationAlertProps
> = ({ isOpen, variant, onConfirm, onClose }) => {
  const { t } = useTranslations();

  const title = t({
    id: "auth-discard-alert.discard-changes-warning-title",
    defaultMessage: "Don’t Lose Your Progress",
  });

  const discardChangesDescription = useMemo(() => {
    if (variant === "logout") {
      return t({
        id: "auth-discard-alert.discard-changes-logout-description",
        defaultMessage:
          "If you go back now, you'll be logged out and all your data will be lost.  😱",
      });
    }

    if (variant === "workspace") {
      return t({
        id: "auth-discard-alert.discard-changes-workspace-description",
        defaultMessage:
          "Your workspace hasn't been created yet. If you leave now, your data will be lost. 😱 If you discard, entered info will not be saved",
      });
    }

    if (variant === "profile") {
      return t({
        id: "auth-discard-alert.discard-changes-profile-description",
        defaultMessage:
          "Your profile hasn't been created yet. If you leave now, your data will be lost. 😱 If you discard, entered info will not be saved",
      });
    }

    return "";
  }, [variant, t]);

  const discardLabel =
    variant === "logout"
      ? t({
          id: "auth-discard-alert.log-out",
          defaultMessage: "Log Out",
        })
      : t({
          id: "auth-discard-alert.leave-without-saving",
          defaultMessage: "Leave Without Saving",
        });

  return (
    <Alert
      isOpen={isOpen}
      img={
        <img
          src={planeGif}
          alt="Discard Changes"
          className="h-[230px] w-[230px]"
        />
      }
      title={title}
      content={discardChangesDescription}
      className="h-[508px] w-[680px]"
      isCloseButtonVisible
      contentContainerClassName="py-0 pt-4"
      header=" "
      headerClassName="border-b-0 absolute top-0 left-0 right-0 z-[51] bg-white rounded-2xl"
      backdropClassName="bg-dark/40 z-[51]"
      buttons={[
        {
          text: discardLabel,
          color: "white",
          onClick: onConfirm,
          className: "text-primary underline",
        },
        {
          text: t({
            id: "common.cancel",
            defaultMessage: "Cancel",
          }),
          className: "w-[200px]",
          color: "primary",
          role: "close",
        },
      ]}
      onRequestClose={onClose}
    />
  );
};
